/**
 * The audio settings menu: skip length on each side, the speed remembered for
 * the book being read, and the three chain toggles.
 *
 * A dumb, book-agnostic component on purpose. It shows one flat set of values
 * and reports a patch; the caller decides which book the rate patch belongs
 * to and forwards it to `setRateFor`, and which fields go straight to
 * `saveAudioPrefs`. That keeps this file usable before the player bar knows
 * how to wire a book id into it.
 */

import { useCallback, useEffect, useRef } from "react";

import { CloseIcon } from "../components/icons";
import { IconButton } from "../components/ui";
import { t, useUiLang } from "../i18n";
import { SKIP_STEPS } from "../lib/audioPrefs";
import { Player } from "../lib/player";

export interface AudioPrefsMenuValues {
  skipBack: number;
  skipForward: number;
  /** The rate that applies to the book currently open. */
  rate: number;
  boost: boolean;
  normalize: boolean;
  trimSilence: boolean;
}

function Pill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={
        "tap whitespace-nowrap flex items-center justify-center rounded-lg px-[var(--spacing-fib8)] text-xs transition-colors duration-150 " +
        (active
          ? "bg-[var(--color-accent-soft)] text-[var(--color-accent)]"
          : "text-[var(--color-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-raised)]")
      }
    >
      {label}
    </button>
  );
}

function Switch({
  label,
  title,
  checked,
  onChange,
}: {
  label: string;
  title: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      title={title}
      onClick={() => onChange(!checked)}
      className="tap whitespace-nowrap flex w-full items-center justify-between gap-[var(--spacing-fib8)] rounded-lg px-[var(--spacing-fib8)] text-left"
    >
      <span className="text-sm text-[var(--color-ink)]">{label}</span>
      <span
        aria-hidden="true"
        className={
          "relative inline-flex h-[var(--spacing-fib21)] w-[var(--spacing-fib34)] shrink-0 rounded-full transition-colors duration-150 " +
          (checked ? "bg-[var(--color-accent)]" : "bg-[var(--color-line)]")
        }
      >
        <span
          className={
            "absolute top-[var(--spacing-fib2)] h-[var(--spacing-fib13)] w-[var(--spacing-fib13)] rounded-full bg-[var(--color-page)] transition-transform duration-150 " +
            (checked
              ? "translate-x-[calc(var(--spacing-fib34)-var(--spacing-fib13)-var(--spacing-fib2))]"
              : "translate-x-[var(--spacing-fib2)]")
          }
        />
      </span>
    </button>
  );
}

export function AudioPrefsMenu({
  open,
  onClose,
  prefs,
  onChange,
}: {
  open: boolean;
  onClose: () => void;
  prefs: AudioPrefsMenuValues;
  onChange: (patch: Partial<AudioPrefsMenuValues>) => void;
}) {
  useUiLang();
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (!panel.current?.contains(event.target as Node)) onClose();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    // Deferred: the click that opened the menu must not immediately close it.
    const timer = window.setTimeout(() => document.addEventListener("mousedown", away), 0);
    document.addEventListener("keydown", escape);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open, onClose]);

  const act = useCallback(
    (patch: Partial<AudioPrefsMenuValues>) => {
      onChange(patch);
      onClose();
    },
    [onChange, onClose],
  );

  if (!open) return null;

  return (
    <div
      ref={panel}
      role="dialog"
      aria-label={t("audioPrefsTitle")}
      className={
        "absolute right-0 bottom-full z-30 mb-[var(--spacing-fib8)] w-72 max-w-[calc(100vw-1.5rem)] rounded-xl " +
        "bg-[var(--color-raised)] p-[var(--spacing-fib13)] shadow-2xl shadow-black/50 ring-1 ring-[var(--color-line)]"
      }
    >
      <div className="mb-[var(--spacing-fib13)] flex items-center justify-between">
        <h2 className="text-sm font-medium text-[var(--color-ink)]">{t("audioPrefsTitle")}</h2>
        <IconButton label={t("audioPrefsClose")} onClick={onClose} className="h-7 w-7 coarse:h-11 coarse:w-11">
          <CloseIcon className="h-4 w-4" />
        </IconButton>
      </div>

      <div className="grid gap-[var(--spacing-fib13)]">
        <div>
          <span className="text-xs text-[var(--color-muted)]">{t("audioPrefsSkipBack")}</span>
          <div
            role="radiogroup"
            aria-label={t("audioPrefsSkipBack")}
            className="mt-[var(--spacing-fib3)] grid grid-cols-7 gap-[var(--spacing-fib2)] rounded-lg bg-[var(--color-surface)] p-[var(--spacing-fib2)]"
          >
            {SKIP_STEPS.map((seconds) => (
              <Pill
                key={seconds}
                label={t("audioPrefsSeconds", { value: seconds })}
                active={prefs.skipBack === seconds}
                onClick={() => act({ skipBack: seconds })}
              />
            ))}
          </div>
        </div>

        <div>
          <span className="text-xs text-[var(--color-muted)]">{t("audioPrefsSkipForward")}</span>
          <div
            role="radiogroup"
            aria-label={t("audioPrefsSkipForward")}
            className="mt-[var(--spacing-fib3)] grid grid-cols-7 gap-[var(--spacing-fib2)] rounded-lg bg-[var(--color-surface)] p-[var(--spacing-fib2)]"
          >
            {SKIP_STEPS.map((seconds) => (
              <Pill
                key={seconds}
                label={t("audioPrefsSeconds", { value: seconds })}
                active={prefs.skipForward === seconds}
                onClick={() => act({ skipForward: seconds })}
              />
            ))}
          </div>
        </div>

        <div>
          <span className="text-xs text-[var(--color-muted)]">{t("audioPrefsSpeed")}</span>
          <div
            role="radiogroup"
            aria-label={t("audioPrefsSpeed")}
            className="mt-[var(--spacing-fib3)] grid grid-cols-5 gap-[var(--spacing-fib2)] rounded-lg bg-[var(--color-surface)] p-[var(--spacing-fib2)]"
          >
            {Player.rates.map((rate) => (
              <Pill
                key={rate}
                label={t("audioPrefsSpeedValue", { value: rate })}
                active={prefs.rate === rate}
                onClick={() => act({ rate })}
              />
            ))}
          </div>
        </div>

        <div>
          <span className="text-xs text-[var(--color-muted)]">{t("audioPrefsProcessing")}</span>
          <div className="mt-[var(--spacing-fib3)] grid gap-[var(--spacing-fib3)]">
            <Switch
              label={t("audioPrefsBoost")}
              title={t("audioPrefsBoostTitle")}
              checked={prefs.boost}
              onChange={(boost) => act({ boost })}
            />
            <Switch
              label={t("audioPrefsNormalize")}
              title={t("audioPrefsNormalizeTitle")}
              checked={prefs.normalize}
              onChange={(normalize) => act({ normalize })}
            />
            <Switch
              label={t("audioPrefsTrimSilence")}
              title={t("audioPrefsTrimSilenceTitle")}
              checked={prefs.trimSilence}
              onChange={(trimSilence) => act({ trimSilence })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
